import React, { useState } from 'react';

const JOURNEY_OPTIONS = [
  {
    key: 'noticing_symptoms',
    emoji: '🌱',
    label: 'Just starting to notice symptoms',
    subtitle: "Something feels off but you're not sure what",
  },
  {
    key: 'seeking_diagnosis',
    emoji: '🔍',
    label: 'Trying to get a diagnosis',
    subtitle: 'Looking for answers from your doctor',
  },
  {
    key: 'started_hrt',
    emoji: '💊',
    label: 'Just started HRT',
    subtitle: "Tracking how it's working for you",
  },
  {
    key: 'adjusting_hrt',
    emoji: '🔄',
    label: 'Adjusting my HRT dose or type',
    subtitle: 'Finding the right balance',
  },
  {
    key: 'managing_symptoms',
    emoji: '✨',
    label: 'Managing symptoms and maintaining',
    subtitle: "Keeping track of what's working",
  },
  {
    key: 'preparing_appointment',
    emoji: '📋',
    label: 'Preparing for a specific appointment',
    subtitle: 'Want a clear record to bring with me',
  },
];

const HRT_TYPES = [
  { key: 'patch', label: 'Patch' },
  { key: 'gel', label: 'Gel' },
  { key: 'spray', label: 'Spray' },
  { key: 'pill', label: 'Pill' },
  { key: 'combined', label: 'Combined' },
  { key: 'other', label: 'Other' },
];

function getWelcomeMessage(journeyKey) {
  const messages = {
    noticing_symptoms: "We'll help you build a clear picture of what's happening. Tracking daily means you'll have a clear record — not just memory — when you talk to your doctor. 🌿",
    seeking_diagnosis: "You're in the right place. Consistent tracking gives you a clear record of how you've been to share with your doctor. Let's build it together. 🌿",
    started_hrt: "Tracking through an HRT journey is one of the most powerful things you can do. We'll show you exactly when things start to shift. 🌿",
    adjusting_hrt: "Dose adjustments are hard to navigate without data. We'll help you see clearly what's changing and when — so you can advocate for yourself. 🌿",
    managing_symptoms: "Staying consistent is everything. We'll keep spotting patterns and make sure your next appointment is always backed by real data. 🌿",
    preparing_appointment: "Perfect. Log daily and we'll help you walk in with a clear, professional summary your doctor can actually use. 🌿",
  };
  return messages[journeyKey] || "Let's get started. 🌿";
}

const HRT_STAGES = ['started_hrt', 'adjusting_hrt'];

function JourneyStageModal({ userEmail, token, onClose, onSaved }) {
  const [journeyStage, setJourneyStage] = useState('');
  const [subStep, setSubStep] = useState('stage'); // 'stage' | 'hrt_details'
  const [hrtType, setHrtType] = useState('');
  const [hrtStartMonth, setHrtStartMonth] = useState('');
  const [hrtChanges, setHrtChanges] = useState('');
  const [saving, setSaving] = useState(false);

  const isHRTStage = HRT_STAGES.includes(journeyStage);

  const handleContinue = () => {
    if (isHRTStage) {
      setSubStep('hrt_details');
    } else {
      handleSave();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const hrt_details = isHRTStage
        ? {
            type: hrtType || null,
            start_month: hrtStartMonth || null,
            recent_changes: hrtChanges || null,
          }
        : null;

      await fetch('/api/save-journey', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          journey_stage: journeyStage,
          hrt_details,
        }),
      });

      localStorage.setItem('apptnote_journey_stage', journeyStage);
      if (onSaved) onSaved(journeyStage);
      onClose();
    } catch (err) {
      console.error('Failed to save journey stage:', err);
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeButton} onClick={onClose}>×</button>

        {subStep === 'stage' ? (
          <>
            <h2 style={styles.title}>Where are you right now?</h2>
            <p style={styles.subtitle}>
              This helps us tailor your experience and insights to exactly
              where you are in your journey.
            </p>

            <div style={styles.journeyOptions}>
              {JOURNEY_OPTIONS.map(({ key, emoji, label, subtitle }) => (
                <button
                  key={key}
                  style={{
                    ...styles.journeyOption,
                    ...(journeyStage === key ? styles.journeyOptionActive : {}),
                  }}
                  onClick={() => setJourneyStage(key)}
                >
                  <span style={styles.journeyEmoji}>{emoji}</span>
                  <div style={styles.journeyText}>
                    <div style={styles.journeyLabel}>{label}</div>
                    <div style={styles.journeySubtitle}>{subtitle}</div>
                  </div>
                  {journeyStage === key && <span style={styles.journeyTick}>✓</span>}
                </button>
              ))}
            </div>

            {journeyStage && (
              <div style={styles.welcomeMessage}>
                <p style={styles.welcomeMessageText}>{getWelcomeMessage(journeyStage)}</p>
              </div>
            )}

            <button
              style={{ ...styles.button, ...(journeyStage ? {} : styles.buttonMuted) }}
              onClick={handleContinue}
              disabled={!journeyStage || saving}
            >
              {isHRTStage ? 'Continue →' : saving ? 'Saving...' : 'Save my journey →'}
            </button>
          </>
        ) : (
          <>
            <button style={styles.backBtn} onClick={() => setSubStep('stage')}>← Back</button>
            <h2 style={styles.title}>Tell us about your HRT</h2>
            <p style={styles.subtitle}>
              Optional, but it helps us show you a clearer before-and-after picture.
            </p>

            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>What type of HRT?</label>
              <div style={styles.hrtTypeGrid}>
                {HRT_TYPES.map(({ key, label }) => (
                  <button
                    key={key}
                    style={{
                      ...styles.hrtTypeBtn,
                      ...(hrtType === key ? styles.hrtTypeBtnActive : {}),
                    }}
                    onClick={() => setHrtType(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>When did you start?</label>
              <input
                type="month"
                value={hrtStartMonth}
                onChange={(e) => setHrtStartMonth(e.target.value)}
                style={styles.monthInput}
                max={new Date().toISOString().slice(0, 7)}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Any recent changes? (optional)</label>
              <input
                type="text"
                value={hrtChanges}
                onChange={(e) => setHrtChanges(e.target.value)}
                placeholder="e.g. switched from patch to gel"
                style={styles.textInput}
                maxLength={120}
              />
            </div>

            <button
              style={styles.button}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save my journey →'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(44, 44, 42, 0.6)', display: 'flex',
    alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  modal: {
    backgroundColor: '#FAF7F2', borderRadius: '20px 20px 0 0',
    padding: '32px 24px 40px', maxWidth: '560px', width: '100%',
    maxHeight: '88vh', overflowY: 'auto', position: 'relative',
    boxShadow: '0 -10px 40px rgba(44,44,42,0.2)',
  },
  closeButton: {
    position: 'absolute', top: '16px', right: '20px',
    background: 'none', border: 'none', fontSize: '26px',
    cursor: 'pointer', color: '#6c8396', lineHeight: '1', padding: '4px 8px',
  },
  backBtn: {
    background: 'none', border: 'none', color: '#6c8396', fontSize: '14px',
    cursor: 'pointer', fontFamily: 'inherit', padding: '0 0 16px', display: 'block',
  },
  title: {
    fontSize: '24px', fontWeight: '800', color: '#2C2C2A',
    marginBottom: '8px', lineHeight: '1.25',
  },
  subtitle: {
    fontSize: '14px', color: '#4a6070', lineHeight: '1.6', marginBottom: '20px',
  },
  journeyOptions: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  journeyOption: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', backgroundColor: 'white',
    border: '1.5px solid #e1e8ed', borderRadius: '12px',
    cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
  },
  journeyOptionActive: { backgroundColor: '#fff8f2', borderColor: '#D4A5A5' },
  journeyEmoji: { fontSize: '20px', flexShrink: 0 },
  journeyText: { flex: 1 },
  journeyLabel: { fontSize: '14px', fontWeight: '600', color: '#2C2C2A', marginBottom: '2px' },
  journeySubtitle: { fontSize: '12px', color: '#6c8396', lineHeight: '1.4' },
  journeyTick: { color: '#7A9E87', fontWeight: '700', fontSize: '15px', flexShrink: 0 },
  welcomeMessage: {
    backgroundColor: '#fff0eb', border: '1px solid #D4A5A5',
    borderRadius: '12px', padding: '12px 16px', marginBottom: '16px',
  },
  welcomeMessageText: { fontSize: '13px', color: '#2C2C2A', lineHeight: '1.7', margin: 0, fontStyle: 'italic' },
  button: {
    width: '100%', padding: '15px', backgroundColor: '#7A9E87', color: 'white',
    border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  buttonMuted: { backgroundColor: '#a8bfae', cursor: 'not-allowed' },
  fieldGroup: { marginBottom: '18px' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: '700', color: '#2C2C2A', marginBottom: '8px' },
  hrtTypeGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  hrtTypeBtn: {
    padding: '8px 16px', backgroundColor: 'white', border: '1.5px solid #e1e8ed',
    borderRadius: '100px', fontSize: '13px', fontWeight: '600', color: '#4a6070',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  hrtTypeBtnActive: { backgroundColor: '#D4A5A5', borderColor: '#D4A5A5', color: 'white' },
  monthInput: {
    width: '100%', padding: '12px 14px', fontSize: '15px',
    border: '1.5px solid #e1e8ed', borderRadius: '10px',
    boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white', color: '#2C2C2A',
  },
  textInput: {
    width: '100%', padding: '12px 14px', fontSize: '15px',
    border: '1.5px solid #e1e8ed', borderRadius: '10px',
    boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white', color: '#2C2C2A',
  },
};

export default JourneyStageModal;
